import axios from 'axios';
import { REST_HOSTNAME } from "../Config";
import { ISentenceScore } from './ScoreApi';
import { ChineseTeacher } from './UserApi';

const axiosInstance = axios.create({
    baseURL: REST_HOSTNAME
});

// Add request interceptor
axiosInstance.interceptors.request.use(
    function (config) {
        const jwt = localStorage.getItem('jwt');
        if (jwt) {
            config.headers['x-access-token'] = jwt; // Add JWT to the headers
        }
        return config;
    },
    function (error) {
        return Promise.reject(error);
    }
);

export interface ICustomSentence {
    id: number,
    text: string,
    pinyin: string[],
    processing_queue?: number,
    processing_error?: string | null,
    playable_teacher_voices?: ChineseTeacher[],
    teacher_voice?: ChineseTeacher,
    audio_request_status?: Partial<Record<ChineseTeacher, {
        state: 'queued' | 'retrying',
        retry_after: string | null,
    }>>,
    recording_count?: number,
    latest_score?: number | null,
    latest_chat_date?: string;
};

export const CustomSentenceApi = {
    getAll: function () {
        return axiosInstance.request<ICustomSentence[]>({
            method: "GET",
            url: `/custom_sentence`
        });
    },
    getBySentenceId: function (sentenceId: number) {
        return axiosInstance.request<ICustomSentence>({
            method: "GET",
            url: `/custom_sentence/sentence/${sentenceId}`
        });
    },
    create: function (customSentence: ICustomSentence) {
        return axiosInstance.request<ICustomSentence>({
            method: "PUT",
            url: `/custom_sentence`,
            data: customSentence
        });
    },
    createBySentenceId: function (sentenceId: number) {
        return axiosInstance.request<ICustomSentence>({
            method: "PUT",
            url: `/custom_sentence/sentence/${sentenceId}`,
            data: sentenceId
        });
    },
    requestAudio: function (sentenceId: number, teacherVoice: ChineseTeacher, retry = false) {
        return axiosInstance.request<ICustomSentence>({
            method: "PUT",
            url: `/custom_sentence/sentence/${sentenceId}/audio-request`,
            data: { teacher_voice: teacherVoice, ...(retry ? { retry: true } : {}) },
        });
    },
    createPublic: function (publicSentenceScoreId: string) {
        return axiosInstance.request<ICustomSentence>({
            method: "PUT",
            url: `/custom_sentence/public/${publicSentenceScoreId}`
        });
    },
    deleteBySentenceId: function (sentenceId: number) {
        return axiosInstance.request<string>({
            method: "DELETE",
            url: `/custom_sentence/sentence/${sentenceId}`,
        });
    },
    getMaleAudio: function (customSentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/male/custom_sentence/${customSentenceId}`,
            responseType: "blob"
        });
    },
    getFemaleAudio: function (customSentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/female/custom_sentence/${customSentenceId}`,
            responseType: "blob"
        });
    },
    getFemale2Audio: function (customSentenceId: number) {
        return axiosInstance.request<Blob>({
            method: "GET",
            url: `/audio/female2/custom_sentence/${customSentenceId}`,
            responseType: "blob"
        });
    },
    scoreCustomSentence: function (customSentenceId: number, audio: any, timeout?: number) {
        return axiosInstance.request<ISentenceScore>({
            method: "PUT",
            url: `/sentence_score/custom_sentence/${customSentenceId}`,
            data: audio,
            timeout,
        });
    },
    getSentenceScore: function (customSentenceId: number) {
        return axiosInstance.request<ISentenceScore[]>({
            method: "GET",
            url: `/sentence_score/custom_sentence/${customSentenceId}`
        });
    },

    deleteSentenceScore: function (customSentenceId: number, sentenceScoreId: number) {
        return axiosInstance.request<ISentenceScore[]>({
            method: "DELETE",
            url: `/sentence_score/custom_sentence/${customSentenceId}/${sentenceScoreId}`
        });
    },
}
